/**
 * React binding over {@link ProctoringManager}.
 */

import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { buildProctoringConfig } from './config'
import { ProctoringManager } from './ProctoringManager'
import type {
    ProctoringConfigInput,
    ProctoringEvents,
    UseProctoringOptions,
    UseProctoringReturn,
} from './types'

export const useProctoring = (options: UseProctoringOptions = {}): UseProctoringReturn => {
    const {
        referenceId,
        tenant,
        profile,
        expectedDurationSeconds,
        options: sdkOptions,
        observability,
        api,
        autoStart = false,
    } = options

    const sdkOptionsKey = sdkOptions ? JSON.stringify(sdkOptions) : ''
    const apiKey = api ? JSON.stringify(api) : ''

    const configInput = useMemo<ProctoringConfigInput | undefined>(() => {
        if (!referenceId) return undefined
        return buildProctoringConfig({
            referenceId,
            tenant,
            profile,
            expectedDurationSeconds,
            options: sdkOptions,
            observability,
            api,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [referenceId, tenant, profile, expectedDurationSeconds, sdkOptionsKey, apiKey])

    const managerRef = useRef<ProctoringManager | null>(null)
    managerRef.current ??= new ProctoringManager()
    const manager = managerRef.current

    const snapshot = useSyncExternalStore(
        manager.subscribe,
        manager.getSnapshot,
        manager.getServerSnapshot
    )

    const blockerSnapshot = useSyncExternalStore(
        manager.subscribeToBlocker,
        manager.getBlockerSnapshot,
        manager.getBlockerSnapshot
    )

    const configRef = useRef(configInput)
    configRef.current = configInput

    useEffect(() => {
        if (configInput) void manager.initialize(configInput)
        return () => {
            void manager.dispose()
        }
    }, [configInput, manager])

    const isReady = snapshot.phase === 'ready'
    const isInitialized = snapshot.phase !== 'uninitialized' && snapshot.phase !== 'destroyed'
    const isRecording = snapshot.sdkState.status === 'recording'

    useEffect(() => {
        if (!autoStart || !isReady || isRecording) return
        void manager.start().catch(() => {})
    }, [autoStart, isReady, isRecording, manager])

    const initialize = useCallback(() => manager.initialize(configRef.current), [manager])
    const requestPermissions = useCallback(() => manager.requestPermissions(), [manager])
    const start = useCallback(() => manager.start(), [manager])
    const pause = useCallback(() => manager.pause(), [manager])
    const resume = useCallback(() => manager.resume(), [manager])
    const stop = useCallback(() => manager.stop(), [manager])
    const destroy = useCallback(() => manager.destroy(), [manager])
    const reset = useCallback(() => manager.reset(), [manager])
    const checkAgainBlocker = useCallback(() => manager.checkAgainBlocker(), [manager])
    const reconnect = useCallback(() => {
        const fresh = referenceId
            ? buildProctoringConfig({
                  referenceId,
                  tenant,
                  profile,
                  expectedDurationSeconds,
                  options: sdkOptions,
                  observability,
                  api,
              })
            : undefined
        return manager.reconnect(fresh)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manager, referenceId, tenant, profile, expectedDurationSeconds, sdkOptionsKey, apiKey])

    const unsubsRef = useRef<Set<() => void>>(new Set())
    const events = useMemo<ProctoringEvents>(() => {
        const bus = manager.getEventBus()
        const track = (rawUnsub: () => void): (() => void) => {
            const wrapped = () => {
                rawUnsub()
                unsubsRef.current.delete(wrapped)
            }
            unsubsRef.current.add(wrapped)
            return wrapped
        }
        return {
            on: (event, handler) => track(bus.on(event, handler)),
            off: (event, handler) => bus.off(event, handler),
            once: (event, handler) => track(bus.once(event, handler)),
        }
    }, [manager])

    useEffect(() => {
        const unsubs = unsubsRef.current
        return () => {
            unsubs.forEach(unsub => unsub())
            unsubs.clear()
        }
    }, [])

    return {
        initialize,
        requestPermissions,
        start,
        pause,
        resume,
        stop,
        destroy,
        reset,
        reconnect,

        state: snapshot,
        status: snapshot.phase,
        error: snapshot.error,

        permissions: snapshot.permissions,
        devices: snapshot.devices,

        isInitialized,
        isActive: snapshot.phase === 'recording',
        isRecording,
        isReady,

        events,
        sdk: manager.getSdk(),

        blocker: blockerSnapshot.blocker,
        blockerChecking: blockerSnapshot.checking,
        blockerFailureReason: blockerSnapshot.lastFailureReason,
        blockerDeniedDevices: blockerSnapshot.deniedDevices,
        checkAgainBlocker,
    }
}
